import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Button as TamaguiButton,
  ButtonProps,
  Text,
  View,
  withStaticProperties,
  XStack,
  YStack,
} from "tamagui";

import { GlowCircle } from "./GlowCircle";

type TabsContextProps<T extends string> = {
  value?: T | undefined;
  onChange?: (value: T) => void;
};

const TabsContext = createContext<TabsContextProps<any>>({});

const useTabsContext = <T extends string>() => {
  const context = useContext<TabsContextProps<T>>(TabsContext);
  if (!context) {
    throw new Error("useTabsContext must be used within a TabsProvider");
  }
  return context as TabsContextProps<T>;
};

export interface TabsProps<T extends string> {
  value?: T;
  defaultValue?: T;
  onChange?: (value: T) => void;
  children?: ReactNode;
}

const TabsComponent = <T extends string>({
  value: externalValue,
  defaultValue,
  onChange: externalOnChange,
  children,
}: TabsProps<T>) => {
  const [value, setValue] = useState<T | undefined>(
    externalValue ?? defaultValue,
  );

  const onChange = (value: T) => {
    setValue(value);
    externalOnChange?.(value);
  };

  useEffect(() => {
    setValue(externalValue);
  }, [externalValue]);

  return (
    <TabsContext.Provider value={{ value, onChange }}>
      <XStack
        borderBottomWidth={1}
        borderBottomColor="$white4"
        flexWrap="wrap"
      >
        {children}
      </XStack>
    </TabsContext.Provider>
  );
};

export interface TabButtonProps<T extends string> extends ButtonProps {
  value: T;
  count?: number;
}

const Button = <T extends string>({
  value,
  count,
  children,
  ...rest
}: TabButtonProps<T>) => {
  const { value: selectedValue, onChange } = useTabsContext<T>();
  const isActive = selectedValue === value;

  const onPress = () => {
    onChange?.(value);
  };

  return (
    <YStack>
      {isActive && (
        <GlowCircle
          size={230}
          color="$hgPrimary"
          gradientOrigin="50% 130%"
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
          }}
        />
      )}

      <TamaguiButton
        variant="outlined"
        gap="$2"
        height={56}
        paddingHorizontal="$5"
        borderRadius={0}
        borderWidth={0}
        hoverStyle={{ borderWidth: 0 }}
        pressStyle={{ borderWidth: 0 }}
        {...rest}
        onPress={onPress}
      >
        <XStack gap="$2" alignItems="center">
          {typeof children === "string" ? (
            <Text fontSize="$4" fontWeight={isActive ? "bold" : undefined}>
              {children}
            </Text>
          ) : (
            children
          )}

          {typeof count !== "undefined" && (
            <View
              height={24}
              justifyContent="center"
              paddingHorizontal="$2"
              backgroundColor={isActive ? "$hgPrimary" : "$transparentBorder"}
              borderWidth={2}
              borderColor="$transparentBorder"
              borderRadius="$12"
            >
              <Text
                fontSize="$3"
                fontWeight="bold"
                color={isActive ? "$black12" : "$white12"}
              >
                {count}
              </Text>
            </View>
          )}
        </XStack>
      </TamaguiButton>

      <View
        position="absolute"
        width="100%"
        bottom={-1}
        height={2}
        backgroundColor={isActive ? "$hgPrimary" : "transparent"}
      />
    </YStack>
  );
};

export const Tabs = withStaticProperties(TabsComponent, {
  Button,
});
